import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Shield, AlertTriangle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useMe } from '@/features/auth/hooks/useAuth'
import { userHasPermission } from '@/lib/permissions'

/**
 * A page for the roles that hold one permission - role-based access in every feature (Andrew,
 * 29 Sep 2026). The server refuses the page's API calls without it; this says so plainly instead of
 * showing a page of errors. It waits for /auth/me, which is where permissions are: deciding on the
 * store's login-shaped user would turn everyone away.
 */
export function PermissionRoute({ permission, children }: { permission: string; children: ReactNode }) {
    const { t } = useTranslation()
    const { data: me, isError, isFetching, refetch } = useMe()

    // "Access Denied" is an answer about the person's role, so it is given only once /auth/me has
    // answered. Until then - still loading, not started yet, or held back (the MFA step) - wait.
    // It used to wait only while the request was in flight, so a page opened before it started, or
    // on a slow machine, told an administrator their role did not include it (30 Sep 2026).
    if (!me) {
        if (isError && !isFetching) {
            return (
                <div className="container mx-auto p-6">
                    <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            <p className="font-medium">{t('auth.permissionCheckFailed.title', "We couldn't check your access")}</p>
                            <p className="mt-2">
                                {t('auth.permissionCheckFailed.body', 'Your account details did not load, so this page cannot tell whether your role includes it. Try again in a moment.')}
                            </p>
                            <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
                                <RefreshCw className="mr-2 h-4 w-4" />
                                {t('auth.permissionCheckFailed.retry', 'Try again')}
                            </Button>
                        </AlertDescription>
                    </Alert>
                </div>
            )
        }
        return (
            <div className="flex h-full items-center justify-center" role="status" aria-live="polite">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
        )
    }

    if (!userHasPermission(me, permission)) {
        return (
            <div className="container mx-auto p-6">
                <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                        <div className="flex items-center space-x-2">
                            <Shield className="h-5 w-5" />
                            <span>{t('auth.permissionDenied.title', 'Access Denied')}</span>
                        </div>
                        <p className="mt-2">
                            {t('auth.permissionDenied.body', "Your role does not include access to this page. An administrator can grant it on the Roles screen.")}
                        </p>
                    </AlertDescription>
                </Alert>
            </div>
        )
    }

    return <>{children}</>
}
